import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import axios from "axios";
import toast from "react-hot-toast";
import { ClientJS } from "clientjs";
import { EyeO, ClosedEye } from "@react-vant/icons";

// import logo from '../../assets/images/logo/logo_white_gif.gif';
import logo from "../../assets/images/logo/logo.png";
import { endpoint } from "../../services/urls";
import { createSession } from "../../utils/session";

function Login() {
  const navigate = useNavigate();
  const client = new ClientJS();
  const fingerprint = client.getFingerprint();

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState("");

  const fk = useFormik({
    initialValues: { username: "", password: "" },
    onSubmit: () => {
      loginFunction({
        username: String(fk.values.username),
        password: fk.values.password,
        lgn_type: 2,
        finger_id: String(fingerprint),
      });
    },
  });

  const loginFunction = async (reqBody) => {
    setLoading(true);
    try {
      const response = await axios.post(endpoint.member_login, reqBody);
      toast(response?.data?.message);
      if (response?.data?.success) {
        localStorage.setItem("token", response?.data?.result?.[0]?.token);
        localStorage.setItem("type", response?.data?.result?.[0]?.user_type);
        createSession(reqBody.username);
        navigate("/Dashboard");
        window.location.reload();
      }
    } catch (e) {
      toast.error("Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-root">
      <svg xmlns="http://www.w3.org/2000/svg" style={{ display: "none" }}>
        <symbol id="icon-email" viewBox="0 0 24 24">
          <path d="M20 4H4C2.9 4 2 4.9 2 6v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
        </symbol>
        <symbol id="icon-editPswIcon" viewBox="0 0 24 24">
          <path d="M12 1C8.676 1 6 3.676 6 7v1H4v15h16V8h-2V7c0-3.324-2.676-6-6-6zm0 2c2.276 0 4 1.724 4 4v1H8V7c0-2.276 1.724-4 4-4zm0 9a2 2 0 1 1 0 4 2 2 0 0 1 0-4z" />
        </symbol>
      </svg>

      <div className="login-bg-grid" />
      <div className="login-glow-tl" />
      <div className="login-glow-br" />
      <div className="login-scanline" />

      <div className="login-card-wrap">
        <div className="login-card">
          <div className="card-inner-glow" />

          <div className="logo-wrap" onClick={() => navigate("/")}>
            <div className="logo-ring">
              <img src={logo} alt="logo" className="logo-img" />
            </div>
            <div className="logo-pulse" />
          </div>

          <div className="title-block">
            <p className="eyebrow">SECURE ACCESS PORTAL</p>
            <h1 className="title">Welcome Back</h1>
            <p className="subtitle">Sign in to your trading account</p>
          </div>

          <form onSubmit={fk.handleSubmit} className="login-form">
            {/* Username field */}
            <div className="field-wrap">
              <label className="field-label">Email / Username</label>
              <div
                className={`field-row ${focused === "username" ? "field-row--active" : ""}`}
              >
                <svg
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="field-icon"
                >
                  <use xlinkHref="#icon-email" />
                </svg>
                <input
                  name="username"
                  value={fk.values.username}
                  onChange={fk.handleChange}
                  onFocus={() => setFocused("username")}
                  onBlur={() => setFocused("")}
                  placeholder="Enter your email"
                  autoComplete="off"
                  className="field-input"
                  style={{
                    WebkitBoxShadow: "0 0 0 1000px transparent inset",
                    WebkitTextFillColor: "#e0f7fa",
                  }}
                />
              </div>
            </div>

            {/* Password field */}
            <div className="field-wrap">
              <label className="field-label">Password</label>
              <div
                className={`field-row ${focused === "password" ? "field-row--active" : ""}`}
              >
                <svg
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="field-icon"
                >
                  <use xlinkHref="#icon-editPswIcon" />
                </svg>
                <input
                  name="password"
                  value={fk.values.password}
                  onChange={fk.handleChange}
                  onFocus={() => setFocused("password")}
                  onBlur={() => setFocused("")}
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  autoComplete="new-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className="field-input"
                  style={{
                    textTransform: "none",
                    WebkitBoxShadow: "0 0 0 1000px transparent inset",
                    WebkitTextFillColor: "#e0f7fa",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="eye-btn"
                >
                  {showPassword ? <ClosedEye /> : <EyeO />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className={`submit-btn ${loading ? "submit-btn--loading" : ""}`}
            >
              <span className="submit-btn__text">
                {loading ? "Authenticating…" : "Sign In"}
              </span>
              {!loading && <span className="submit-btn__arrow">→</span>}
              {loading && <span className="submit-btn__spinner" />}
            </button>

            {/* Divider */}
            <div className="or-divider">
              <span className="or-line" />
              <span className="or-text">NEW HERE?</span>
              <span className="or-line" />
            </div>

            {/* Register */}
            <NavLink to="/register" className="register-btn">
              Create an Account
            </NavLink>
          </form>

          {/* Footer note */}
          <p className="security-note">
            <span className="security-dot" />
            Secured connection
          </p>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Sora:wght@300;400;500;600;700&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .login-root {
          min-height: 100vh;
          background: #000;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          font-family: 'Sora', sans-serif;
        }

        /* ── Background grid ── */
        .login-bg-grid {
          position: fixed; inset: 0; pointer-events: none;
          background-image:
            linear-gradient(rgba(0,229,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,229,255,0.03) 1px, transparent 1px);
          background-size: 44px 44px;
          z-index: 2;
        }

        /* ── Ambient glows ── */
        .login-glow-tl {
          position: fixed; top: -160px; left: -120px;
          width: 520px; height: 520px; border-radius: 50%;
          background: radial-gradient(circle, rgba(0,229,255,0.09) 0%, transparent 70%);
          pointer-events: none; z-index: 2;
        }
        .login-glow-br {
          position: fixed; bottom: -120px; right: -80px;
          width: 400px; height: 400px; border-radius: 50%;
          background: radial-gradient(circle, rgba(0,180,200,0.06) 0%, transparent 70%);
          pointer-events: none; z-index: 2;
        }

        /* ── Scanlines ── */
        .login-scanline {
          position: fixed; inset: 0; pointer-events: none;
          background: repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            rgba(0,0,0,0.06) 2px,
            rgba(0,0,0,0.06) 4px
          );
          z-index: 2;
        }

        /* ── Corner brackets ── */
        .corner {
          position: fixed; width: 28px; height: 28px;
          pointer-events: none; opacity: 0.22;
        }
        .corner-tl { top: 16px; left: 16px;
          border-top: 1.5px solid #00e5ff; border-left: 1.5px solid #00e5ff; }
        .corner-tr { top: 16px; right: 16px;
          border-top: 1.5px solid #00e5ff; border-right: 1.5px solid #00e5ff; }
        .corner-bl { bottom: 16px; left: 16px;
          border-bottom: 1.5px solid #00e5ff; border-left: 1.5px solid #00e5ff; }
        .corner-br { bottom: 16px; right: 16px;
          border-bottom: 1.5px solid #00e5ff; border-right: 1.5px solid #00e5ff; }

        /* ── Card wrapper ── */
        .login-card-wrap {
          width: 100%; max-width: 420px;
          padding: 20px 16px 40px;
          position: relative; z-index: 10;
          animation: cardIn 0.55s cubic-bezier(0.22,1,0.36,1) both;
          isolation: isolate;
        }
        @keyframes cardIn {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ── Card ── */
        .login-card {
          background: rgba(0, 0, 0, 0.92);
          border: 1px solid rgba(0,229,255,0.18);
          border-radius: 22px;
          padding: 36px 28px 28px;
          position: relative;
          box-shadow:
            0 0 0 1px rgba(0,229,255,0.06) inset,
            0 40px 80px rgba(0,0,0,0.35),
            0 0 60px rgba(0,229,255,0.06);
        }

        /* Top shimmer */
        .card-inner-glow {
          position: absolute; top: 0; left: 50%;
          transform: translateX(-50%);
          width: 55%; height: 1px;
          background: linear-gradient(90deg, transparent, rgba(0,229,255,0.55), transparent);
        }

        /* ── Logo ── */
        .logo-wrap {
          display: flex; justify-content: center;
          margin-bottom: 22px; position: relative;
        }
        .logo-ring {
          width: 150px; height: 60px;
          background: rgba(0,229,255,0.04);
          display: flex; align-items: center; justify-content: center;
          position: relative; z-index: 1;
          box-shadow: 0 0 28px rgba(0,229,255,0.08);
        }
        .logo-img { height: px;}
        .logo-pulse {
          position: absolute; top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          width: 160px; height: 66px;;
          border: 1px solid rgba(0,229,255,0.1);
          animation: logoPulse 2.8s ease-in-out infinite;
        }
        @keyframes logoPulse {
          0%, 100% { transform: translate(-50%,-50%) scale(1); opacity: 0.7; }
          50%       { transform: translate(-50%,-50%) scale(1.14); opacity: 0; }
        }

        /* ── Title ── */
        .title-block { text-align: center; margin-bottom: 26px; }
        .eyebrow {
          font-family: 'Space Mono', monospace;
          font-size: 9px; letter-spacing: 0.22em;
          color: rgba(0,229,255,0.38); margin-bottom: 8px;
          text-transform: uppercase;
        }
        .title {
          font-size: 22px; font-weight: 700; font-family: 'Sora', sans-serif;
          color: #e0f7fa; letter-spacing: -0.02em; margin-bottom: 5px;
        }
        .subtitle {
          font-size: 12px; color: rgba(100,180,200,0.45); font-weight: 400;
        }

        /* ── Form ── */
        .login-form { display: flex; flex-direction: column; gap: 14px; }

        .field-wrap { display: flex; flex-direction: column; gap: 6px; }

        .field-label {
          font-family: 'Space Mono', monospace;
          font-size: 9px; letter-spacing: 0.14em;
          text-transform: uppercase; color: rgba(0,229,255,0.4);
        }

        .field-row {
          display: flex; align-items: center; gap: 10px;
          background: rgba(0,229,255,0.025);
          border: 1px solid rgba(0,229,255,0.11);
          border-radius: 12px; padding: 0 14px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .field-row:hover:not(.field-row--active) {
          border-color: rgba(0,229,255,0.22);
        }
        .field-row--active {
          border-color: rgba(0,229,255,0.48);
          box-shadow: 0 0 0 3px rgba(0,229,255,0.06), 0 0 20px rgba(0,229,255,0.05);
        }

        .field-icon { color: rgba(0,229,255,0.4); flex-shrink: 0; }

        .field-input {
          flex: 1; min-width: 0; background: transparent;
          color: #e0f7fa; font-size: 13px; font-family: 'Sora', sans-serif;
          padding: 14px 0; outline: none; border: none;
        }
        .field-input::placeholder { color: rgba(0,229,255,0.22); }

        .eye-btn {
          background: none; border: none; cursor: pointer;
          color: rgba(0,229,255,0.3); flex-shrink: 0;
          display: flex; align-items: center;
          transition: color 0.15s; padding: 2px;
        }
        .eye-btn:hover { color: rgba(0,229,255,0.75); }

        /* ── Forgot ── */
        .forgot-row { display: flex; justify-content: flex-end; margin-top: -4px; }
        .forgot-link {
          font-size: 11px; font-family: 'Space Mono', monospace;
          letter-spacing: 0.04em; color: rgba(0,229,255,0.4);
          text-decoration: none; transition: color 0.15s;
        }
        .forgot-link:hover { color: rgba(0,229,255,0.85); }

        /* ── Submit ── */
        .submit-btn {
          margin-top: 4px;
          width: 100%; height: 48px; border-radius: 12px;
          border: none; cursor: pointer;
          background: linear-gradient(90deg, #00e5ff, #00b8d4);
          color: #020c12;
          font-family: 'Space Mono', monospace;
          font-size: 12px; font-weight: 700; letter-spacing: 0.14em;
          text-transform: uppercase;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          position: relative; overflow: hidden;
          transition: transform 0.15s, box-shadow 0.2s, opacity 0.2s;
          box-shadow: 0 4px 24px rgba(0,229,255,0.22);
        }
        .submit-btn::before {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
          transform: translateX(-100%);
          transition: transform 0.5s;
        }
        .submit-btn:hover:not(:disabled)::before { transform: translateX(100%); }
        .submit-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 32px rgba(0,229,255,0.32);
        }
        .submit-btn:active:not(:disabled) { transform: translateY(0); }
        .submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .submit-btn__arrow { font-size: 16px; }
        .submit-btn__spinner {
          width: 16px; height: 16px; border-radius: 50%;
          border: 2px solid rgba(2,12,18,0.25); border-top-color: #020c12;
          animation: spin 0.7s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* ── Divider ── */
        .or-divider {
          display: flex; align-items: center; gap: 10px; margin: 2px 0;
        }
        .or-line { flex: 1; height: 1px; background: rgba(0,229,255,0.07); }
        .or-text {
          font-family: 'Space Mono', monospace;
          font-size: 9px; letter-spacing: 0.2em; color: rgba(0,229,255,0.22);
        }

        /* ── Register button ── */
        .register-btn {
          display: flex; align-items: center; justify-content: center;
          height: 46px; border-radius: 12px;
          border: 1px solid rgba(0,229,255,0.16);
          background: rgba(0,229,255,0.03);
          color: rgba(0,229,255,0.65);
          font-family: 'Space Mono', monospace;
          font-size: 11px; font-weight: 700;
          letter-spacing: 0.1em; text-transform: uppercase;
          text-decoration: none;
          transition: all 0.2s;
        }
        .register-btn:hover {
          background: rgba(0,229,255,0.08);
          border-color: rgba(0,229,255,0.38);
          color: #00e5ff;
        }

        /* ── Security note ── */
        .security-note {
          display: flex; align-items: center; justify-content: center; gap: 7px;
          margin-top: 22px;
          font-size: 10px; color: rgba(0,229,255,0.18);
          font-family: 'Space Mono', monospace; letter-spacing: 0.06em;
        }
        .security-dot {
          width: 5px; height: 5px; border-radius: 50%;
          background: rgba(0,229,255,0.4);
          animation: blink 2.2s ease-in-out infinite;
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.15; }
        }
      `}</style>
    </div>
  );
}

export default Login;
